import { enrollmentWindowService } from '@/data/services/content/enrollment-window-service'
import { NO_ACTIVE_OFFER_REASON } from '@/data/services/content/enrollment-window-service-types'
import { Account } from '@/domain/usecases'
import { checkFlagEnabled, FeatureFlagClient, FeatureFlagName } from '@/infra/feature-flag'
import { OnboardingOptions } from '@/domain/usecases/onboarding/onboarding-options'
import { UserContentDetails } from '@/domain/usecases/content/user-content-details'
import { UserOnboarding } from '@/domain/usecases/onboarding/user-onboarding'
import { UserProfileAssessment } from '@/domain/usecases/profile-assessment/user-profile-assessment'
import { queryClient } from '@/infra/http'
import { makeSkillWebContentPageRedirectUrl } from '@/main/factories/http'
import {
  importRemote,
  contentDetailsPromise,
  enrollmentContentInfoPromise
} from '@/main/routes/helpers/loaders'
import PathConstants from '@/main/routes/paths'
import { redirect } from 'react-router-dom'

/**
 * Loads content enrollment data
 * Complex loader that fetches account, onboarding, content details and profile assessment
 * Redirects if content is restricted
 */
export async function loadContentEnrollment(id: number) {
  // Check if content is accessible
  const userEnrollmentContentInfo = await enrollmentContentInfoPromise(queryClient, id, true)
  if (!userEnrollmentContentInfo || userEnrollmentContentInfo.restricted) {
    return redirect(makeSkillWebContentPageRedirectUrl(id))
  }

  // O `:id` da rota não é necessariamente um productId: o `EnrollCta` monta a URL como
  // `offer.offerId ?? productId`, e com oferta selecionada — o caminho feliz — o que chega aqui é um
  // offerId. Produto e oferta compartilham o espaço de ids, então consultar a rota por produto com esse
  // valor ou erra por 404 ou acerta um produto sem relação nenhuma. O productId vem do detalhe do
  // conteúdo; a consulta encadeia nele e corre em paralelo com o resto do loader.
  const userContentDetailsPromise = contentDetailsPromise(queryClient, id, true)
  const noActiveOfferPromise = hasNoActiveOffer(userContentDetailsPromise)

  const [
    remoteAccountProfile,
    onboardingOptions,
    userContentDetails,
    [userOnboarding, userProfileAssessment]
  ] = await Promise.all([
    importRemote<Account>('makeRemoteAccountProfile', queryClient),
    importRemote<OnboardingOptions>('makeRemoteOnboardingOptions', queryClient).then(remote =>
      remote.getStep4()
    ),
    userContentDetailsPromise,
    Promise.allSettled([getUserOnboarding(), getUserProfileAssessment()]).then(items =>
      items.map(item => (item.status === 'fulfilled' ? item.value : null))
    )
  ])

  // Produto ativo sem nenhuma oferta (TS-5314): com a flag ligada o endpoint de conteúdo responde 200 e
  // `restricted: false`, então a checagem acima deixa a rota abrir por URL direta, bookmark ou botão
  // voltar — e lá dentro o step 3 dispara o POST de matrícula sozinho, depois da biometria facial, para
  // receber um 404. Volta para a PDV, onde o CTA de "avise-me" é o caminho válido desse estado.
  if (await noActiveOfferPromise) {
    const { productId } = userContentDetails
    return redirect(
      PathConstants.Routes.CONTENT.computeValue({ content: { id: productId, productId } })
    )
  }

  // Double check restriction after all promises resolve
  if (userEnrollmentContentInfo.restricted) {
    return redirect(makeSkillWebContentPageRedirectUrl(id))
  }

  return {
    remoteAccountProfile,
    accountProfile: await remoteAccountProfile.loader(),
    onboardingOptions,
    userContentDetails,
    userEnrollmentContentInfo,
    userSkills: (userOnboarding as UserOnboarding.Model | null)?.interest?.skills || [],
    userProfileAssessment: userProfileAssessment as UserProfileAssessment.Model | null
  }
}

/**
 * Flag do "avise-me sem oferta" para uso em loader.
 *
 * `checkFlagEnabled` lê o mapa em memória do LaunchDarkly, que só é populado depois que a árvore React
 * chama `syncWithClient`. Em hard load — URL direta, bookmark, botão voltar — o loader roda antes disso e
 * o default `false` desligaria o gate justamente no acesso que ele existe para cobrir.
 *
 * Com o LD ainda não pronto, consulta assim mesmo: a chave é a mesma que o backend lê, então só há
 * `NO_ACTIVE_OFFER` na resposta quando a flag está de fato ligada dos dois lados. Um round trip a mais
 * nesse intervalo é mais barato que abrir a rota inválida.
 */
function isProductInterestEnabled(): boolean {
  if (!FeatureFlagClient.isReady()) return true
  return checkFlagEnabled(FeatureFlagName.ENABLE_PRODUCT_INTEREST_WITHOUT_OFFER)
}

/**
 * Produto ativo sem nenhuma oferta ativa.
 *
 * Recebe a promise do detalhe do conteúdo em vez do id da rota: o `:id` pode ser um offerId, e a rota por
 * produto precisa do productId de verdade.
 *
 * Qualquer falha da consulta responde `false`: o gate é uma proteção de UX, não pode transformar uma
 * indisponibilidade da API em redirect de uma matrícula legítima.
 */
async function hasNoActiveOffer(
  userContentDetails: Promise<UserContentDetails.Model>
): Promise<boolean> {
  if (!isProductInterestEnabled()) return false

  try {
    const { productId } = await userContentDetails
    if (!productId) return false

    const window = await enrollmentWindowService.fetchEnrollmentWindow(productId, {
      byProduct: true
    })
    return window.reason === NO_ACTIVE_OFFER_REASON
  } catch {
    return false
  }
}

async function getUserOnboarding() {
  const remote = await importRemote<UserOnboarding>('makeRemoteUserOnboarding', queryClient)
  return remote.get()
}

async function getUserProfileAssessment() {
  const remote = await importRemote<UserProfileAssessment>(
    'makeRemoteUserProfileAssessment',
    queryClient
  )
  return remote.getLatest()
}
